import "./ImageOrVideoFullWidthBlock.scss";
import { RefObject, useEffect, useRef, useState } from "react";
import { ImageOrVideoFullWidthBlockProps } from "/Models/Generated/ImageOrVideoFullWidthBlockProps";
import {
  Picture,
  VideoWithPoster,
  ArrowsOutSimple,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
} from "/App.Components";
import { createSpreadableBem } from "/Helpers/createBem";
import { useModal } from "/Components/Shared/ModalProvider/ModalProvider";
import FillViewportKeepAspectRatio from "/Helpers/FillViewportKeepAspectRatio";

interface Props extends ImageOrVideoFullWidthBlockProps {
  insideModal?: boolean;
  source?: RefObject<HTMLDivElement | null>;
  reduceHeight?: boolean;
  initialSlideIndex?: number;
  showZoomButton?: boolean;
}

const ImageOrVideoFullWidthBlock = (props: Props) => {
  const {
    video,
    descriptionText,
    insideModal,
    source,
    reduceHeight,
    showZoomButton = true,
    zoomButtonAriaLabel,
    previousButtonAriaLabel,
    nextButtonAriaLabel,
    zoomedImage,
    carouselImages,
    zoomedCarouselImages,
    initialSlideIndex = 0,
  } = props;

  const [posterClicked, setPosterClicked] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(initialSlideIndex);

  const videoHasCopyright =
    video &&
    (video.copyrightLabel ?? "") !== "" &&
    (video.copyright ?? "") !== "";

  const regularSlides =
    (carouselImages ?? []).length > 0 ? (carouselImages ?? []) : [];

  const publishedSlides = regularSlides;

  const slides =
    insideModal && publishedSlides.length > 0
      ? publishedSlides.map(
          (slide, idx) =>
            zoomedCarouselImages?.[idx] ??
            (idx === 0 ? zoomedImage : undefined) ??
            slide,
        )
      : publishedSlides;

  const hasCarousel = !video && publishedSlides.length > 1;
  const currentImage = !video ? slides[currentSlideIndex] : undefined;

  const imageHasCopyright =
    currentImage &&
    (currentImage.copyrightLabel ?? "") !== "" &&
    (currentImage.copyright ?? "") !== "";

  const zoomedVideo = video
    ? { ...video, posterImage: video.zoomPosterImage }
    : undefined;

  const thisRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!insideModal || !thisRef.current || !source?.current) return;

    const adjustWidth = () =>
      FillViewportKeepAspectRatio(62, reduceHeight ? 10 : 40, thisRef, source);

    adjustWidth();
    window.addEventListener("resize", adjustWidth);

    return () => window.removeEventListener("resize", adjustWidth);
  }, []);

  useEffect(() => {
    setCurrentSlideIndex(initialSlideIndex);
  }, [initialSlideIndex]);

  useEffect(() => {
    if (currentSlideIndex > Math.max(publishedSlides.length - 1, 0)) {
      setCurrentSlideIndex(0);
    }
  }, [publishedSlides.length, currentSlideIndex]);

  const hasText =
    hasCarousel ||
    imageHasCopyright ||
    videoHasCopyright ||
    (descriptionText ?? "") !== "";
  const showZoomControl = !insideModal && showZoomButton;

  const { openModal } = useModal();

  const goToPreviousSlide = () => {
    if (!hasCarousel) return;

    setCurrentSlideIndex((prev) =>
      prev === 0 ? publishedSlides.length - 1 : prev - 1,
    );
  };

  const goToNextSlide = () => {
    if (!hasCarousel) return;

    setCurrentSlideIndex((prev) =>
      prev === publishedSlides.length - 1 ? 0 : prev + 1,
    );
  };

  const bem = createSpreadableBem("image-or-video-full-width-block-norad");

  return (
    <div {...bem("")} ref={thisRef}>
      <div {...bem("__thumbnail")}>
        {currentImage && !video && (
          <Picture
            {...currentImage}
            pictureAspectRatio="16/9"
            pictureBehaviour="Cover"
          />
        )}
        {video && (
          <VideoWithPoster
            posterClicked={posterClicked}
            setPosterClicked={() => setPosterClicked(!posterClicked)}
            {...video}
            posterBehaviour="Cover"
            posterAspectRatio="16/9"
          />
        )}
      </div>

      {(hasText || showZoomControl) && (
        <div {...bem("__text-and-zoom", { "--inside-modal": insideModal })}>
          <div {...bem("__caption")}>
            {hasCarousel && (
              <div {...bem("__carousel-controls")}>
                <button
                  type="button"
                  {...bem("__carousel-prev")}
                  onClick={goToPreviousSlide}
                  aria-label={previousButtonAriaLabel}
                >
                  <CaretCircleLeftIcon
                    size="32"
                    thickness="Light"
                    color="#5B0337"
                  />
                </button>
                <div {...bem("__dots")}>
                  {publishedSlides.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      {...bem("__dot", {
                        "--active": idx === currentSlideIndex,
                      })}
                      aria-label={`${idx + 1}`}
                      aria-current={
                        idx === currentSlideIndex ? "true" : undefined
                      }
                      disabled={idx === currentSlideIndex}
                      tabIndex={idx === currentSlideIndex ? -1 : 0}
                      onClick={() => setCurrentSlideIndex(idx)}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  {...bem("__carousel-next")}
                  onClick={goToNextSlide}
                  aria-label={nextButtonAriaLabel}
                >
                  <CaretCircleRightIcon
                    size="32"
                    thickness="Light"
                    color="#5B0337"
                  />
                </button>
              </div>
            )}

            {(descriptionText ?? "") !== "" && (
              <p {...bem("__description")}>{descriptionText}</p>
            )}

            <div {...bem("__copyright-and-zoom")}>
              <div {...bem("__copyright-row")}>
                {imageHasCopyright && (
                  <span {...bem("__copyright")}>
                    {currentImage.copyrightLabel}
                  </span>
                )}
                {imageHasCopyright && (
                  <span {...bem("__copyright")}>{currentImage.copyright}</span>
                )}
                {videoHasCopyright && (
                  <span {...bem("__copyright")}>{video.copyrightLabel}</span>
                )}
                {videoHasCopyright && (
                  <span {...bem("__copyright")}>{video.copyright}</span>
                )}
              </div>

              {showZoomControl && (
                <button
                  {...bem("__zoom")}
                  aria-label={zoomButtonAriaLabel}
                  onClick={() =>
                    openModal(
                      <div {...bem("__modal-wrapper")}>
                        <ImageOrVideoFullWidthBlock
                          {...props}
                          insideModal
                          source={thisRef}
                          reduceHeight={!hasText}
                          video={zoomedVideo}
                          initialSlideIndex={currentSlideIndex}
                        />
                      </div>,
                    )
                  }
                >
                  <span>
                    <ArrowsOutSimple size="20" thickness="Reg" />
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageOrVideoFullWidthBlock;
